import { Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { useTelegramBackButton } from '@/hooks/use-telegram-back-button'
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Package,
  GraduationCap,
  Shield,
  Settings,
  Menu,
  X,
  Home,
  UserCheck,
  ClipboardList
} from 'lucide-react'
import { useState } from 'react'

interface AdminLayoutProps {
  children: React.ReactNode
}

export function AdminLayout({ children }: AdminLayoutProps) {
  useTelegramBackButton()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const navigation = [
    {
      name: '儀表板',
      href: '/admin/dashboard',
      icon: LayoutDashboard,
    },
    {
      name: '用戶管理',
      href: '/admin/users',
      icon: Users,
    },
    {
      name: '學生管理',
      href: '/admin/students',
      icon: UserCheck,
    },
    {
      name: '課程管理',
      href: '/admin/courses',
      icon: BookOpen,
    },
    {
      name: '套餐管理',
      href: '/admin/combos',
      icon: Package,
    },
    {
      name: '教師管理',
      href: '/admin/teachers',
      icon: GraduationCap,
    },
    {
      name: '報名管理',
      href: '/admin/enrollments',
      icon: ClipboardList,
    },
    {
      name: '管理員',
      href: '/admin/administrators',
      icon: Shield,
    },
    {
      name: '外觀管理',
      href: '/admin/appearance',
      icon: Settings,
    },
  ]

  const isActive = (href: string) => {
    return location.pathname === href
  }

  return (
    <div className="admin-layout">
      <style>{`
        .admin-layout {
          min-height: 100vh;
          background-color: hsl(var(--background));
        }
        
        .admin-sidebar {
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          width: 256px;
          background-color: hsl(var(--card));
          border-right: 1px solid hsl(var(--border));
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
          z-index: 50;
          display: flex;
          flex-direction: column;
          transform: translateX(0);
          transition: transform 0.3s ease-in-out;
        }
        
        .admin-sidebar.open {
          transform: translateX(0);
        }
        
        .admin-sidebar-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 64px;
          padding: 0 16px;
          border-bottom: 1px solid hsl(var(--border));
        }
        
        .admin-sidebar-brand {
          display: flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          color: inherit;
        }
        
        .admin-sidebar-brand-text {
          font-size: 18px;
          font-weight: 600;
          color: hsl(var(--foreground));
        }
        
        .admin-sidebar-nav {
          flex: 1;
          padding: 24px 16px;
          overflow-y: auto;
        }
        
        .admin-nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 12px;
          margin-bottom: 4px;
          border-radius: 8px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          color: hsl(var(--muted-foreground));
          transition: all 0.15s ease;
        }
        
        .admin-nav-item:hover {
          background-color: hsl(var(--accent));
          color: hsl(var(--foreground));
        }
        
        .admin-nav-item.active {
          background-color: hsl(var(--ring) / 0.12);
          color: hsl(var(--ring));
        }
        
        .admin-sidebar-footer {
          padding: 16px;
          border-top: 1px solid hsl(var(--border));
        }
        
        .admin-main-container {
          margin-left: 256px;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }
        
        .admin-header {
          background-color: hsl(var(--card));
          border-bottom: 1px solid hsl(var(--border));
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 24px;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        }
        
        .admin-content {
          flex: 1;
          padding: 24px;
        }
        
        .admin-mobile-backdrop {
          position: fixed;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 40;
          display: none;
        }
        
        .admin-mobile-backdrop.show {
          display: block;
        }
        
        .admin-mobile-menu-btn {
          display: none;
        }
        
        @media (max-width: 1023px) {
          .admin-sidebar {
            position: fixed;
            transform: translateX(-100%);
          }
          
          .admin-sidebar.open {
            transform: translateX(0);
          }
          
          .admin-main-container {
            margin-left: 0;
          }
          
          .admin-mobile-menu-btn {
            display: flex;
          }
        }
      `}</style>

      {/* Mobile backdrop */}
      <div 
        className={`admin-mobile-backdrop ${sidebarOpen ? 'show' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <div className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <Link to="/admin/dashboard" className="admin-sidebar-brand">
            <Shield className="w-8 h-8 text-blue-600" />
            <span className="admin-sidebar-brand-text">管理後台</span>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="admin-mobile-menu-btn"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <nav className="admin-sidebar-nav">
          {navigation.map((item) => {
            const Icon = item.icon
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`admin-nav-item ${isActive(item.href) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon className="w-5 h-5" />
                {item.name}
              </Link>
            )
          })}
        </nav>

        <div className="admin-sidebar-footer">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Button variant="ghost" className="w-full justify-start">
              <Settings className="w-4 h-4 mr-3" />
              系統設定
            </Button>
            <Link to="/">
              <Button variant="ghost" className="w-full justify-start">
                <Home className="w-4 h-4 mr-3" />
                回到首頁
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="admin-main-container">
        {/* Top header */}
        <header className="admin-header">
          <Button
            variant="ghost"
            size="sm"
            className="admin-mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-4 h-4" />
          </Button>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ fontSize: '14px', color: 'hsl(var(--muted-foreground))' }}>
              管理員面板
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="admin-content">
          {children}
        </main>
      </div>
    </div>
  )
}